import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDiamondMatrixThunk,
  setStoneRateThunk,
} from "../../store/thunks/rateThunks";

const StoneRateGrid = ({ branchCode }) => {
  const dispatch = useDispatch();
  const { stoneRates, loading, ui } = useSelector((state) => state.rate);
  const { user } = useSelector((state) => state.auth);
  const [selectedShape, setSelectedShape] = useState("ROUND");
  const [editingCell, setEditingCell] = useState(null);

  // Use passed branchCode or fall back to selected branch or primary branch
  const currentBranch =
    branchCode || ui.selectedBranch || user?.branchAccess?.primaryBranch;

  const shapes = ["ROUND", "PRINCESS", "EMERALD", "OVAL", "MARQUISE", "PEAR"];
  const colors = ["D", "E", "F", "G", "H", "I", "J"];
  const clarities = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2"];

  useEffect(() => {
    if (currentBranch) {
      dispatch(getDiamondMatrixThunk({ branchCode: currentBranch }));
    }
  }, [dispatch, currentBranch]);

  const handleCellEdit = async (color, clarity, value) => {
    await dispatch(
      setStoneRateThunk({
        stoneType: "DIAMOND",
        branchCode: currentBranch,
        diamondSpecs: {
          shape: selectedShape,
          colorGrade: color,
          clarityGrade: clarity,
          sizeRange: { min: 0.01, max: 5.0 },
        },
        pricing: {
          baseRate: parseFloat(value),
          currency: "INR",
          unit: "CARAT",
        },
      })
    );
    setEditingCell(null);
  };

  const getRateValue = (color, clarity) => {
    return (
      stoneRates.diamondMatrix?.[selectedShape]?.[color]?.[clarity]?.baseRate ||
      0
    );
  };

  if (!currentBranch) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8 text-gray-500">
          Please select a branch to manage stone rates
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold mb-4">Diamond Rate Matrix</h2>
          {currentBranch && (
            <span className="text-sm text-gray-600">
              Branch: <span className="font-semibold">{currentBranch}</span>
            </span>
          )}
        </div>

        <div className="flex gap-2 mb-4">
          {shapes.map((shape) => (
            <button
              key={shape}
              onClick={() => setSelectedShape(shape)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                selectedShape === shape
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {shape}
            </button>
          ))}
        </div>
      </div>

      {loading.stoneRates ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Color/Clarity
                </th>
                {clarities.map((clarity) => (
                  <th
                    key={clarity}
                    className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase"
                  >
                    {clarity}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {colors.map((color) => (
                <tr key={color}>
                  <td className="px-4 py-2 font-medium text-sm text-gray-900">
                    {color}
                  </td>
                  {clarities.map((clarity) => {
                    const cellKey = `${color}-${clarity}`;
                    const value = getRateValue(color, clarity);

                    return (
                      <td key={clarity} className="px-4 py-2 text-center">
                        {editingCell === cellKey ? (
                          <input
                            type="number"
                            defaultValue={value}
                            onBlur={(e) =>
                              handleCellEdit(color, clarity, e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleCellEdit(color, clarity, e.target.value);
                              }
                            }}
                            className="w-20 px-2 py-1 border border-blue-500 rounded text-sm"
                            autoFocus
                          />
                        ) : (
                          <button
                            onClick={() => setEditingCell(cellKey)}
                            className="w-20 px-2 py-1 text-sm hover:bg-gray-100 rounded"
                          >
                            {value ? `₹${value.toLocaleString()}` : "-"}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        <p>* Rates are per carat in INR</p>
        <p>* Click on any cell to edit the rate</p>
      </div>
    </div>
  );
};

export default StoneRateGrid;
